/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionsServiceRepositoryImpl } from '../impls/ActionsServiceRepositoryImpl';
import { ActionsServiceRepositoryImplV2 } from '../impls/ActionsServiceRepositoryImplV2';
import { IActionsService } from '../interfaces/IActionsService';
import { IActionsServiceRepository } from '../interfaces/IActionsServiceRepository';
import {
    TActionList,
    TActionsContentParams,
    TActionsContentScreenContentIdParams,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParams,
    TActionsParamsUCScreenDetails,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TServicesParams,
    TUnifiedCollectionScreenDetails,
} from '../types';

export class ActionsService implements IActionsService {
    private oActionsServiceRepository: IActionsServiceRepository;

    private oActionsServiceRepositoryV2: IActionsServiceRepository;

    public constructor(params: TServicesParams) {
        this.oActionsServiceRepository = new ActionsServiceRepositoryImpl(params);
        this.oActionsServiceRepositoryV2 = new ActionsServiceRepositoryImplV2(params);
    }

    // eslint-disable-next-line class-methods-use-this
    private removeCastAllEpisodeAndMayAlsoLike(contentScreenDetailsOfferResponse: any) {
        const responseData = contentScreenDetailsOfferResponse;
        responseData.detailList.details = responseData.detailList.details.filter(
            ({ presentation }: { presentation: any }) =>
                presentation.presentationId !== 'SEASON_OR_YEAR_DETAIL' &&
                presentation.presentationId !== 'CAST_DETAIL' &&
                presentation.presentationId !== 'MAY_ALSO_LIKE_DETAIL',
        );
        return responseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsContentActions(
        actionParams: TActionsContentScreenContentIdParams,
    ) {
        const contentScreenDetailsContentIdResponse =
            await this.oActionsServiceRepository.fetchContentScreenDetailsContentActions(
                actionParams,
            );
        const filteredResponseData = this.removeCastAllEpisodeAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ) {
        const contentScreenDetailsContentIdResponse =
            await this.oActionsServiceRepository.fetchContentScreenDetailsOfferActions(
                actionsParams,
            );
        const filteredResponseData = this.removeCastAllEpisodeAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ) {
        const contentScreenDetailsContentIdResponse =
            await this.oActionsServiceRepository.fetchContentScreenDetailsRecordingActions(
                actionsParams,
            );
        const filteredResponseData = this.removeCastAllEpisodeAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        return this.oActionsServiceRepository.fetchOfferActions(actionsParams);
    }

    /**
     * @inheritdoc
     */
    public async getOfferActionsV2(actionsParams: TActionsParams): Promise<TActionList> {
        return this.oActionsServiceRepositoryV2.fetchOfferActions(actionsParams);
    }

    /**
     * @inheritdoc
     */
    public async getRecordingActionsV2(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        return this.oActionsServiceRepositoryV2.fetchRecordingActions(actionsParams);
    }

    /**
     * @inheritdoc
     */
    public async getUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        return this.oActionsServiceRepository.fetchUnifiedCollectionActions(actionsParams);
    }

    /**
     * @inheritdoc
     */
    public getUCScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        return this.oActionsServiceRepository.fetchUnifiedCollectionScreenDetailsActions(
            actionsParams,
        );
    }

    /**
     * @inheritdoc
     */
    public async getVodOfferActionsV2(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        return this.oActionsServiceRepositoryV2.fetchVodOfferActions(actionsParams);
    }

    /**
     * @inheritdoc
     */
    public async getContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        return this.oActionsServiceRepository.fetchContentActions(actionsParams);
    }
}
